import {
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
  Children,
  cloneElement,
} from 'react'
import * as S from './VisibilityDetector.styles'
import useElementIntersection from '../../hooks/useElementIntersection'

const VISIBILITY_THRESHOLD = 0.5

export default function VisibilityDetector({
  onVisible,
  triggerDelay = 0,
  children,
  className,
  singleElementRef = false,
}: PropsWithChildren<{
  onVisible: () => void
  triggerDelay?: number
  className?: string
  singleElementRef?: boolean
}>) {
  const { elementRef, intersectionRatio } =
    useElementIntersection<HTMLDivElement>({
      threshold: [VISIBILITY_THRESHOLD],
    })
  const liveIntersectionRef = useRef(intersectionRatio)
  const [wasVisible, setWasVisible] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    liveIntersectionRef.current = intersectionRatio
    if (!wasVisible && intersectionRatio >= VISIBILITY_THRESHOLD) {
      timeoutRef.current = setTimeout(() => {
        if (
          liveIntersectionRef.current >= VISIBILITY_THRESHOLD &&
          !wasVisible
        ) {
          setWasVisible(true)
          onVisible()
        }
      }, triggerDelay)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [intersectionRatio, wasVisible, triggerDelay, onVisible])

  if (singleElementRef) {
    if (Children.count(children) !== 1) {
      throw new Error(
        'singleElementRef is true but there is more than one child'
      )
    }

    return cloneElement(
      children as React.ReactElement<{ ref?: React.Ref<HTMLDivElement> }>,
      {
        ref: elementRef,
      }
    )
  }

  return (
    <div ref={elementRef} className={className}>
      {
        children || (
          <S.EmptyBottom />
        ) /* Create an empty child if no children given */
      }
    </div>
  )
}
